import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';

import React, { useState, useEffect } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Switch, Box, Typography, Button, TextField, Snackbar, Alert } from '@mui/material';
import PaginacionControl from '../components/PaginacionControl';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import { GridOn } from '@mui/icons-material';
import axios from 'axios';

const API = '/api';

const esSuperAdmin = (() => {
  try { return JSON.parse(localStorage.getItem('usuario') || 'null')?.es_superadmin === 1; }
  catch { return false; }
})();

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', nombres: '', apellidos: '', email: '', perfil_id: '', password: '' });
  const [resetDialog, setResetDialog] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [page, setPage] = useState(0);
  const [saveError, setSaveError] = useState('');
  const [snackMsg, setSnackMsg] = useState('');

  const fetchUsuarios = async () => {
    const res = await axios.get(`${API}/usuarios`);
    setUsuarios(res.data);
  };
  const fetchPerfiles = async () => {
    const res = await axios.get(`${API}/usuarios/perfiles/activos`);
    setPerfiles(res.data);
  };
  useEffect(() => {
    fetchUsuarios();
    fetchPerfiles();
  }, []);

  const handleOpen = (user = null) => {
    setEditing(user);
    setForm(user ? { ...user, password: '' } : { username: '', nombres: '', apellidos: '', email: '', perfil_id: '', password: '' });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/usuarios/${editing.id}`, form);
      } else {
        await axios.post(`${API}/usuarios`, form);
      }
      fetchUsuarios();
      setOpen(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Error al guardar usuario');
    }
  };

  const handleActive = async (user, activo) => {
    await axios.patch(`${API}/usuarios/${user.id}/activo`, { activo });
    fetchUsuarios();
  };

  const handleResetDialog = user => { setResetUser(user); setResetDialog(true); setResetPassword(''); };
  const handleResetPassword = async () => {
    await axios.patch(`${API}/usuarios/${resetUser.id}/reset-password`, { password: resetPassword });
    setResetDialog(false);
    setSnackMsg(`Contraseña de "${resetUser.username}" restablecida`);
  };

  const handleDeleteDialog = user => { setDeleteUser(user); setDeleteDialog(true); };
  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API}/usuarios/${deleteUser.id}`);
      setDeleteDialog(false);
      setSnackMsg(`Usuario "${deleteUser.username}" eliminado permanentemente`);
      fetchUsuarios();
    } catch (err) {
      setDeleteDialog(false);
      setSaveError(err.response?.data?.error || 'Error al eliminar usuario');
    }
  };

  // Exportar Excel
  const handleExportExcel = async () => {
    const { exportGenericWorkbook } = await import('../utils/excel/genericWorkbook');
    const filtered = usuarios.filter(u =>
      u.username.toLowerCase().includes(searchValue.toLowerCase()) ||
      (u.nombres || '').toLowerCase().includes(searchValue.toLowerCase()) ||
      (u.apellidos || '').toLowerCase().includes(searchValue.toLowerCase())
    );
    const rows = (rowsPerPage === -1 ? filtered : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage))
      .map(u => ({
        username: u.username,
        nombres: u.nombres,
        apellidos: u.apellidos,
        email: u.email,
        perfil: u.perfil,
        estadoStr: u.activo ? 'ACTIVO' : 'INACTIVO'
      }));
    await exportGenericWorkbook({
      titulo: 'REPORTE - USUARIOS',
      sheetName: 'Usuarios',
      tableName: 'tblUsuarios',
      fileName: 'Usuarios',
      columns: [
        { key: 'username', label: 'Usuario', width: 20 },
        { key: 'nombres', label: 'Nombres', width: 24 },
        { key: 'apellidos', label: 'Apellidos', width: 24 },
        { key: 'email', label: 'Email', width: 30 },
        { key: 'perfil', label: 'Perfil', width: 20 },
        { key: 'estadoStr', label: 'Estado', width: 14, isEstado: true }
      ],
      rows,
      searchText: searchValue,
      usuario: ''
    });
  };

  const filteredUsuarios = usuarios.filter(u =>
    u.username.toLowerCase().includes(searchValue.toLowerCase()) ||
    (u.nombres || '').toLowerCase().includes(searchValue.toLowerCase()) ||
    (u.apellidos || '').toLowerCase().includes(searchValue.toLowerCase())
  );
  const pagedUsuarios = rowsPerPage === -1
    ? filteredUsuarios
    : filteredUsuarios.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Gestión de Usuarios</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Buscar..."
            value={searchValue}
            onChange={e => { setSearchValue(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <Box sx={{ color: 'text.secondary', mr: 1 }}><SearchIcon /></Box> }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="success"
            startIcon={<GridOn />}
            onClick={handleExportExcel}
            sx={{ minWidth: 120 }}
          >Exportar Excel</Button>
          {esSuperAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ ml: { xs: 0, sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>
              Nuevo Usuario
            </Button>
          )}
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{ width: 100 }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 350, sm: 900, md: 1100 } }}>
            <TableHead>
              <TableRow>
                <TableCell align="center">Usuario</TableCell>
                <TableCell align="center">Nombres</TableCell>
                <TableCell align="center">Apellidos</TableCell>
                <TableCell align="center">Email</TableCell>
                <TableCell align="center">Perfil</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedUsuarios.map((u) => (
                <TableRow key={u.id}>
                  <TableCell align="center">{u.username}</TableCell>
                  <TableCell align="center">{u.nombres}</TableCell>
                  <TableCell align="center">{u.apellidos}</TableCell>
                  <TableCell align="center">{u.email}</TableCell>
                  <TableCell align="center">{u.perfil}</TableCell>
                  <TableCell align="center">
                    {esSuperAdmin
                      ? <Switch checked={!!u.activo} onChange={e => handleActive(u, e.target.checked)} color="success" />
                      : <Switch checked={!!u.activo} disabled color="success" />
                    }
                  </TableCell>
                  <TableCell align="center">
                    {esSuperAdmin && (
                      <>
                        <IconButton color="primary" onClick={() => handleOpen(u)} title="Editar"><EditIcon /></IconButton>
                        <IconButton color="warning" onClick={() => handleResetDialog(u)} title="Resetear contraseña"><LockResetIcon /></IconButton>
                        {u.username !== 'admin' && (
                          <IconButton color="error" onClick={() => handleDeleteDialog(u)} title="Eliminar permanentemente"><DeleteIcon /></IconButton>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginacionControl page={page} rowsPerPage={rowsPerPage} count={filteredUsuarios.length} onPageChange={setPage} />
      </Box>

      {/* ── Diálogo Crear/Editar ── */}
      <Dialog open={open} onClose={(_, r) => r !== 'backdropClick' && handleClose()} disableEscapeKeyDown={false}>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            {editing ? 'Editar Usuario' : 'Nuevo Usuario'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Usuario" name="username" value={form.username} onChange={handleChange} fullWidth disabled={!!editing} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField margin="dense" label="Nombres" name="nombres" value={form.nombres} onChange={handleChange} fullWidth sx={{ flex: 4 }} />
            <TextField margin="dense" label="Apellidos" name="apellidos" value={form.apellidos} onChange={handleChange} fullWidth sx={{ flex: 6 }} />
          </Box>
          <TextField margin="dense" label="Email" name="email" value={form.email} onChange={handleChange} fullWidth sx={{ mt: 1 }} />
          <Autocomplete
            options={perfiles}
            getOptionLabel={option => option.nombre || ''}
            value={perfiles.find(p => p.id === form.perfil_id) || null}
            onChange={(_, newValue) => setForm({ ...form, perfil_id: newValue ? newValue.id : '' })}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => <TextField {...params} label="Perfil" margin="dense" fullWidth />}
          />
          {!editing && <TextField margin="dense" label="Contraseña" name="password" type="password" value={form.password} onChange={handleChange} fullWidth />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" variant="contained">Cancelar</Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} color="primary">Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* ── Diálogo Resetear Contraseña ── */}
      <Dialog open={resetDialog} onClose={() => setResetDialog(false)}>
        <DialogTitle>Resetear Contraseña</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Nueva Contraseña" type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog(false)}>Cancelar</Button>
          <Button onClick={handleResetPassword} variant="contained">Resetear</Button>
        </DialogActions>
      </Dialog>

      {/* ── Diálogo Confirmar Eliminación Física ── */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>Eliminar Usuario Permanentemente</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de eliminar al usuario <strong>"{deleteUser?.username}"</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Esta acción es irreversible. Se eliminarán también sus permisos y asignaciones.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} variant="outlined">Cancelar</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" startIcon={<DeleteIcon />}>
            Eliminar definitivamente
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!saveError} autoHideDuration={5000} onClose={() => setSaveError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setSaveError('')} sx={{ width: '100%' }}>{saveError}</Alert>
      </Snackbar>
      <Snackbar open={!!snackMsg} autoHideDuration={4000} onClose={() => setSnackMsg('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackMsg('')} sx={{ width: '100%' }}>{snackMsg}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Usuarios;
